import type { SairAccount } from "@opencode/schema/sair-account"
import { createStore } from "solid-js/store"
import { onCleanup } from "solid-js"
import type { CloudConnection, CloudWorkspace } from "./cloud-context"
import { CloudApiError, createCloudApi } from "./cloud-api"
import { CloudSignInError, createCloudSignIn, type CloudPendingSignIn } from "./cloud-sign-in"

export function createCloudWorkspace(input: {
  api: ReturnType<typeof createCloudApi>
  origin: string
  clientID: string
  signIn: ReturnType<typeof createCloudSignIn>
  onSignedOut?: () => void
}): CloudWorkspace {
  const [account, setAccount] = createStore<CloudWorkspace["account"]["state"]>({
    busy: false,
    checking: true,
    loadError: false,
    actionError: false,
    revokeError: false,
  })
  const [state, setState] = createStore<CloudWorkspace["state"]>({ busy: false, phase: "checking" })
  const life = {
    abort: new AbortController(),
    action: new AbortController(),
    generation: 0,
    audience: "",
    active: false,
    refreshing: undefined as Promise<void> | undefined,
    opening: undefined as Promise<CloudConnection> | undefined,
    openingFailed: false,
    renewing: undefined as Promise<void> | undefined,
    disposed: false,
    peerBusy: false,
    login: undefined as CloudPendingSignIn | undefined,
  }
  const signal = () => AbortSignal.any([life.abort.signal, life.action.signal])
  const code = (error: unknown) =>
    error instanceof CloudSignInError
      ? error.code
      : error instanceof CloudApiError
        ? error.serverMessage === "expired_token"
          ? "expired_token"
          : error.code
        : "network"
  const reset = (notify = false) => {
    life.generation++
    life.action.abort()
    life.action = new AbortController()
    life.opening = undefined
    life.openingFailed = false
    life.renewing = undefined
    life.refreshing = undefined
    life.active = false
    life.login = undefined
    setState({ connection: undefined, busy: false, phase: "signed_out", error: undefined, errorMessage: undefined })
    setAccount({
      status: { auth: "signed_out", model: "unavailable" },
      busy: false,
      checking: false,
      loadError: false,
      actionError: false,
    })
    if (notify) input.onSignedOut?.()
  }
  const signedIn = (profile: SairAccount.Profile) => {
    if (
      account.status?.profile &&
      (account.status.profile.sairId !== profile.sairId ||
        account.status.profile.remoteWorkspace?.audience !== life.audience)
    )
      reset(true)
    setAccount({
      status: {
        auth: "authenticated",
        model: "ready",
        profile: {
          ...profile,
          remoteWorkspace: { url: input.origin, audience: life.audience },
        },
      },
      checking: false,
      loadError: false,
      actionError: false,
    })
  }
  async function refresh() {
    if (life.refreshing) return life.refreshing
    if (account.busy || account.status?.attempt || life.disposed) return
    const generation = life.generation
    const current = signal()
    setAccount("checking", true)
    const pending = Promise.all([input.api.config({ signal: current }), input.api.auth({ signal: current })])
      .then(async ([config, auth]) => {
        if (current.aborted || generation !== life.generation) return
        life.audience = config.audience
        if (!auth.authenticated) {
          reset(!!account.status?.profile)
          return
        }
        signedIn(auth.profile)
        if (!state.connection && !life.openingFailed) await ensureRemote()
      })
      .catch((error) => {
        if (current.aborted || generation !== life.generation) return
        setAccount("loadError", true)
        setState("error", code(error))
      })
      .finally(() => {
        if (life.refreshing === pending) life.refreshing = undefined
        if (generation === life.generation) setAccount("checking", false)
      })
    life.refreshing = pending
    return pending
  }
  function ensureRemote(caller?: AbortSignal, force = false) {
    if (caller?.aborted) return Promise.reject(caller.reason)
    if (life.openingFailed && !force)
      return Promise.reject(new CloudApiError("unavailable", undefined, state.errorMessage))
    if (state.connection && state.phase === "ready" && !force) return Promise.resolve(state.connection)
    if (life.opening) return life.opening
    const profile = account.status?.profile
    if (account.status?.auth !== "authenticated" || !profile || !life.audience)
      return Promise.reject(new Error("Cloud account is not authenticated"))
    const generation = life.generation
    const previous = {
      failed: life.openingFailed,
      state: { phase: state.phase, error: state.error, errorMessage: state.errorMessage },
    }
    const timeout = AbortSignal.timeout(300_000)
    const current = AbortSignal.any([signal(), timeout, ...(caller ? [caller] : [])])
    setState({ busy: true, phase: "starting", error: undefined, errorMessage: undefined })
    life.openingFailed = false
    const pending = (async () => {
      const lease = await input.api.connect(input.clientID, { signal: current })
      if (lease.workspace.error?.trim()) throw new CloudApiError("unavailable", 202, lease.workspace.error)
      let expiresAt = lease.expiresAt
      while (!current.aborted) {
        const details = await input.api.details({ signal: current })
        current.throwIfAborted()
        if (generation !== life.generation) throw new DOMException("Account changed", "AbortError")
        if (details.error?.trim()) throw new CloudApiError("unavailable", 200, details.error)
        setState("phase", details.state)
        if (details.state === "ready") {
          if (!details.id || details.id !== lease.workspace.id) throw new CloudApiError("invalid_response", 200)
          const connection: CloudConnection = {
            key: JSON.stringify([life.audience, profile.sairId, details.id]),
            url: input.origin,
            subject: profile.sairId,
            audience: life.audience,
            workspaceID: details.id,
            directory: details.details.directory,
            expiresAt,
          }
          setState({ connection, error: undefined })
          return connection
        }
        if (["failed", "unavailable", "stopped", "stopping"].includes(details.state))
          throw new CloudApiError("unavailable", 503)
        await pause(2000, current)
        const renewed = await input.api.renew(false, { signal: current })
        expiresAt = renewed.expiresAt
      }
      throw current.reason
    })()
      .catch((error) => {
        const timedOut = timeout.aborted && current.reason === timeout.reason
        const failure = caller?.aborted ? new CloudApiError("aborted") : timedOut ? new CloudApiError("timeout") : error
        if (life.disposed || generation !== life.generation) throw failure
        if (caller?.aborted) {
          life.openingFailed = state.connection ? previous.failed : true
          setState(
            state.connection ? previous.state : { phase: "unavailable", error: "aborted", errorMessage: undefined },
          )
          throw failure
        }
        if (!current.aborted || timedOut) {
          life.openingFailed = true
          setState({
            phase: "unavailable",
            error: code(failure),
            errorMessage: failure instanceof CloudApiError ? failure.serverMessage : undefined,
          })
          if (failure instanceof CloudApiError && failure.status === 401) reset(true)
        }
        throw failure
      })
      .finally(() => {
        if (life.opening === pending) {
          life.opening = undefined
          setState("busy", false)
        }
      })
    life.opening = pending
    return pending
  }
  async function renew() {
    if (life.renewing) return life.renewing
    if (!state.connection || state.busy || life.openingFailed || account.busy || life.disposed) return
    const current = signal()
    const generation = life.generation
    const active = life.active
    life.active = false
    const pending = input.api
      .renew(active, { signal: current })
      .then((value) => {
        if (current.aborted || generation !== life.generation) return
        setState("phase", value.state)
        setState("error", undefined)
        setState("connection", (connection) => (connection ? { ...connection, expiresAt: value.expiresAt } : undefined))
      })
      .catch(async (error) => {
        if (current.aborted || generation !== life.generation) return
        setState("error", code(error))
        if (!(error instanceof CloudApiError) || error.status !== 401) return
        const auth = await input.api.auth({ signal: current }).catch(() => undefined)
        if (current.aborted || generation !== life.generation) return
        if (auth && !auth.authenticated) reset(true)
        else if (auth?.authenticated && auth.profile.sairId !== account.status?.profile?.sairId) {
          reset(true)
          signedIn(auth.profile)
        } else if (auth?.authenticated) {
          setState("phase", "unavailable")
          await ensureRemote(undefined, true).catch(() => undefined)
        } else setState("phase", "unavailable")
      })
      .finally(() => {
        if (life.renewing === pending) life.renewing = undefined
      })
    life.renewing = pending
    return pending
  }
  async function start() {
    if (account.busy) return
    reset()
    const current = signal()
    setAccount({ busy: true, actionError: false })
    broadcast("signing-in")
    try {
      input.signIn.check()
      input.signIn.clear(true)
    } catch (error) {
      failedSignIn(error)
      broadcast("signed-out")
      return
    }
    await synchronized(async () => {
      if (current.aborted) return
      try {
        const config = await input.api.config({ signal: current })
        life.audience = config.audience
        const attempt = await input.api.start(input.clientID, { signal: current })
        current.throwIfAborted()
        // Keep the launch UI busy until navigation replaces this document.
        life.login = input.signIn.start(attempt, config.audience, input.clientID)
      } catch (error) {
        if (current.aborted) return
        failedSignIn(error)
        broadcast("signed-out")
      }
    })
  }
  function waiting(attempt: CloudPendingSignIn): SairAccount.Status {
    return { auth: "authorizing", model: "unavailable", attempt: { ...attempt, attemptID: attempt.state } }
  }
  function failedSignIn(error: unknown) {
    life.login = undefined
    setAccount({
      busy: false,
      checking: false,
      actionError: !(error instanceof CloudSignInError && error.code === "cancelled"),
      status: {
        auth: "signed_out",
        model: "unavailable",
        ...(error instanceof CloudSignInError && error.code === "cancelled"
          ? {}
          : { error: { code: code(error), message: "" } }),
      },
    })
    setState("error", code(error))
  }
  async function finishSignIn(attempt: CloudPendingSignIn) {
    const current = signal()
    life.login = attempt
    setState("phase", attempt.returned ? "authorizing" : "signed_out")
    setAccount({ status: waiting(attempt), checking: false })
    await synchronized(async () => {
      try {
        const check = () => {
          current.throwIfAborted()
          if (!input.signIn.active(attempt) || attempt.clientID !== input.clientID)
            throw new CloudSignInError("sign_in_return")
          if (Date.now() >= attempt.expiresAt) throw new CloudSignInError("expired_token")
        }
        check()
        const config = await input.api.config({ signal: current })
        check()
        if (config.audience !== attempt.audience) throw new CloudSignInError("sign_in_return")
        life.audience = config.audience
        const auth = await input.api.auth({ signal: current })
        check()
        if (auth.authenticated) {
          input.signIn.clear()
          life.login = undefined
          signedIn(auth.profile)
          broadcast("signed-in")
          await ensureRemote()
          return
        }
        let interval = attempt.interval
        while (!current.aborted) {
          await pause(interval * 1000, current)
          check()
          const result = await input.api.poll({ signal: current }).catch((error) => {
            // The existing gateway wraps Access device errors in its error string.
            if (error instanceof CloudApiError && error.serverMessage === "SAIR access_denied")
              throw new CloudSignInError("access_denied")
            if (error instanceof CloudApiError && error.serverMessage === "SAIR expired_token")
              throw new CloudSignInError("expired_token")
            if (
              error instanceof CloudApiError &&
              ["network", "timeout", "unavailable", "rate_limited"].includes(error.code)
            ) {
              interval = Math.max(interval, 5)
              return undefined
            }
            throw error
          })
          check()
          if (!result) continue
          if (result.state === "pending") {
            interval = result.interval
            continue
          }
          if (result.clientID !== input.clientID) throw new CloudSignInError("sign_in_return")
          input.signIn.clear()
          life.login = undefined
          signedIn(result.profile)
          broadcast("signed-in")
          await ensureRemote()
          return
        }
      } catch (error) {
        if (current.aborted) return
        if (account.status?.auth === "authenticated") return
        if (error instanceof CloudSignInError) {
          try {
            input.signIn.clear()
          } catch (failure) {
            failedSignIn(failure)
            broadcast("signed-out")
            return
          }
        }
        failedSignIn(error)
        broadcast("signed-out")
      }
    })
  }
  const activity = () => {
    life.active = true
    if (state.connection && ["stopped", "stopping"].includes(state.phase))
      void ensureRemote(undefined, true).catch(() => undefined)
  }
  const channel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel("omm.cloud.account")
  const broadcast = (message: string) => {
    if (!life.disposed) channel?.postMessage(message)
  }
  if (channel)
    channel.onmessage = (event) => {
      if (event.data === "signing-in" || event.data === "logout-pending") {
        reset(true)
        life.peerBusy = true
        setAccount("busy", true)
      }
      if (event.data === "signed-out" || event.data === "signed-in") {
        life.peerBusy = false
        reset(true)
        if (event.data === "signed-in") void refresh()
      }
      if (event.data === "logout-failed") {
        life.peerBusy = false
        logoutFailed()
      }
    }
  function logoutFailed() {
    setAccount({
      busy: false,
      actionError: true,
      status: {
        auth: "unavailable",
        model: "unavailable",
        error: { code: "logout_storage_error", message: "" },
      },
    })
  }
  const focus = async () => {
    if (!life.peerBusy || !navigator.locks) return
    const locks = await navigator.locks.query()
    if (locks.held?.some((lock) => lock.name === "omm.cloud.account")) return
    life.peerBusy = false
    setAccount("busy", false)
    void refresh()
  }
  const timer = setInterval(() => void renew(), 3000)
  const authTimer = setInterval(() => {
    if (state.connection) void refresh()
  }, 20_000)
  window.addEventListener("keydown", activity, { passive: true })
  window.addEventListener("pointerdown", activity, { passive: true })
  window.addEventListener("focus", focus)
  onCleanup(() => {
    life.disposed = true
    life.abort.abort()
    clearInterval(timer)
    clearInterval(authTimer)
    channel?.close()
    window.removeEventListener("keydown", activity)
    window.removeEventListener("pointerdown", activity)
    window.removeEventListener("focus", focus)
    // Tabs share a browser lease. Closing one tab must not release another tab's lease.
  })
  try {
    const pending = input.signIn.resume()
    if (pending) void finishSignIn(pending)
    else void refresh()
  } catch (error) {
    failedSignIn(error)
  }
  return {
    state,
    available: () => account.status?.auth === "authenticated",
    ensureRemote,
    reconnect: async (caller) => {
      caller.throwIfAborted()
      await renew()
      caller.throwIfAborted()
      if (!state.connection || state.phase !== "ready") throw new Error("Workspace is not ready")
      return { url: state.connection.url }
    },
    details: (key) => {
      if (state.connection?.key !== key) return Promise.reject(new Error("Cloud account changed"))
      return input.api.details({ signal: signal() })
    },
    sshKey: async (key) => {
      const connection = state.connection
      const profile = account.status?.profile
      if (
        !connection ||
        connection.key !== key ||
        account.status?.auth !== "authenticated" ||
        profile?.sairId !== connection.subject ||
        profile?.remoteWorkspace?.audience !== connection.audience
      )
        throw new Error("Cloud account changed")
      const generation = life.generation
      const result = await input.api.sshKey({ signal: signal() })
      if (
        generation !== life.generation ||
        state.connection?.key !== key ||
        result.filename !== `${connection.workspaceID}.key`
      )
        throw new Error("Cloud account changed")
      return result
    },
    resize: async (key, value) => {
      if (state.connection?.key !== key) throw new Error("Cloud account changed")
      await input.api.resize(value, { signal: signal() })
      setState("phase", "resizing")
    },
    account: {
      state: account,
      authenticated: () => account.status?.auth === "authenticated",
      refresh,
      start,
      reopen: () => {
        try {
          if (!life.login) return
          input.signIn.reopen(life.login)
          setAccount({ status: { auth: "signed_out", model: "unavailable" }, busy: true })
        } catch (error) {
          failedSignIn(error)
        }
      },
      cancel: async () => {
        reset()
        try {
          input.signIn.clear()
          broadcast("signed-out")
        } catch (error) {
          failedSignIn(error)
        }
        return account.status
      },
      retry: async () => {
        try {
          const pending = input.signIn.resume()
          if (pending) await finishSignIn(pending)
          else await refresh()
        } catch (error) {
          failedSignIn(error)
        }
        return account.status
      },
      logout: async () => {
        if (account.busy) return
        reset(true)
        setAccount("busy", true)
        broadcast("logout-pending")
        try {
          input.signIn.clear(true)
        } catch {
          logoutFailed()
          broadcast("logout-failed")
          return
        }
        await synchronized(async () => {
          try {
            const result = await input.api.logoutBrowser({ signal: life.abort.signal })
            setAccount("revokeError", !result.released)
            broadcast("signed-out")
          } catch {
            logoutFailed()
            broadcast("logout-failed")
          } finally {
            setAccount("busy", false)
          }
        })
      },
    },
  }
}

function synchronized(run: () => Promise<void>) {
  return navigator.locks ? navigator.locks.request("omm.cloud.account", run) : run()
}

function pause(milliseconds: number, signal: AbortSignal) {
  signal.throwIfAborted()
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort)
      resolve()
    }, milliseconds)
    const abort = () => {
      clearTimeout(timer)
      reject(signal.reason)
    }
    signal.addEventListener("abort", abort, { once: true })
  })
}
